import {
  type DependencyList,
  type RefObject,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import {
  type GutterItem,
  layoutGutter,
  type PlacedItem,
} from './_lib/gutter-layout'

/**
 * One SECTION's worth of gutter items: a ref to the section's DOM element
 * plus every comment/composer anchored to it. All of a section's items
 * inherit the section's single measured anchorY, so "same section ⇒
 * identical anchorY" holds by construction — which is exactly the equality
 * `layoutGutter` groups on for its R/L alternation rule (spec §8.1).
 */
export interface GutterSection {
  ref: RefObject<HTMLElement | null>
  items: Omit<GutterItem, 'anchorY'>[]
}

export interface UseGutterLayoutResult {
  placed: PlacedItem[]
  containerWidth: number
  /** Attach to the relatively-positioned column the gutters are offset from. */
  containerRef: RefObject<HTMLDivElement | null>
}

/**
 * Measures each section's y position (relative to the container) via refs,
 * then runs the pure `layoutGutter` engine (./_lib/gutter-layout.ts).
 * Re-measures on mount, on container resize (ResizeObserver), and whenever
 * `deps` changes.
 *
 * Usage:
 * ```tsx
 * const { placed, containerWidth, containerRef } = useGutterLayout(
 *   [
 *     { ref: pacingRef, items: pacingComments },
 *     { ref: codeRef, items: [...codeComments, composerItem] },
 *   ],
 *   [comments, composer, openDiffId, rewriteOpenId, expandedId],
 * )
 * ```
 *
 * Deps contract (IMPORTANT for consumers):
 * - The effect can only measure once `containerRef.current` is mounted; if
 *   the container isn't in the DOM yet (e.g. the review body is collapsed
 *   or conditionally rendered), the hook silently returns `placed: []` /
 *   `containerWidth: 0` for that render. Because a plain ref assignment
 *   does not re-run effects, whatever state TOGGLES the container into the
 *   DOM (e.g. `expandedId`) MUST appear in `deps` so the hook re-measures
 *   once the container mounts. Sections whose ref is unmounted are skipped
 *   the same way — include their visibility state in `deps` too.
 * - Any state that changes an item's estimated height (pending status,
 *   open diff, rewrite panel, body edits) or the item set itself must be
 *   in `deps`; the hook cannot see those changes otherwise.
 *
 * Estimate-based by design (spec §18) — this hook does not measure rendered
 * card heights, only section anchor positions. Do not add measured-height
 * refinement in this phase.
 */
export function useGutterLayout(
  sectionRefs: GutterSection[],
  deps: DependencyList = [],
): UseGutterLayoutResult {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [placed, setPlaced] = useState<PlacedItem[]>([])

  useLayoutEffect(() => {
    const container = containerRef.current
    // No container yet ⇒ nothing to measure against. Per the deps contract
    // above, the state that mounts the container must be in `deps` so this
    // effect re-runs once it appears.
    if (!container) return

    const measure = () => {
      const containerRect = container.getBoundingClientRect()
      setContainerWidth(containerRect.width)

      const items: GutterItem[] = []
      for (const section of sectionRefs) {
        const el = section.ref.current
        if (!el) continue
        const anchorY = el.getBoundingClientRect().top - containerRect.top
        for (const item of section.items) {
          items.push({ ...item, anchorY })
        }
      }

      setPlaced(layoutGutter(items, { containerWidth: containerRect.width }))
    }

    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(container)
    return () => observer.disconnect()
    // biome-ignore lint/correctness/useExhaustiveDependencies: `deps` is the caller-supplied recompute trigger (comment/composer/diff/visibility state); `sectionRefs` entries are ref objects whose identity is stable across renders.
  }, deps)

  return { placed, containerWidth, containerRef }
}
